import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import SEO from '../components/SEO';
import * as FiIcons from 'react-icons/fi';

const { FiTrendingUp, FiBarChart3, FiDollarSign, FiCalendar, FiUser, FiArrowUpRight } = FiIcons;

function Portfolio() {
  const portfolioItems = [
    {
      client: 'Tech Startup Investment',
      category: 'Stock Trading',
      return: '+145%',
      period: '18 months',
      description: 'Strategic investment in emerging technology companies with focus on AI and blockchain.',
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=500&h=300&fit=crop',
      tags: ['Technology', 'Growth', 'Long-term']
    },
    {
      client: 'EUR/USD Trading Strategy',
      category: 'Forex Trading',
      return: '+89%',
      period: '12 months',
      description: 'Systematic forex trading approach utilizing technical analysis and market sentiment.',
      image: 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=500&h=300&fit=crop',
      tags: ['Forex', 'Technical Analysis', 'Short-term']
    },
    {
      client: 'Crypto Portfolio Diversification',
      category: 'Cryptocurrency',
      return: '+234%',
      period: '24 months',
      description: 'Diversified cryptocurrency portfolio with focus on major coins and DeFi protocols.',
      image: 'https://images.unsplash.com/photo-1518546305927-5a555bb7020d?w=500&h=300&fit=crop',
      tags: ['Cryptocurrency', 'DeFi', 'High-risk']
    },
    {
      client: 'Retirement Portfolio Management',
      category: 'Portfolio Management',
      return: '+67%',
      period: '36 months',
      description: 'Conservative portfolio strategy focused on steady growth and capital preservation.',
      image: 'https://images.unsplash.com/photo-1559526324-4b87b5e36e44?w=500&h=300&fit=crop',
      tags: ['Conservative', 'Retirement', 'Diversified']
    },
    {
      client: 'Energy Sector Investment',
      category: 'Sector Trading',
      return: '+112%',
      period: '15 months',
      description: 'Focused investment strategy in renewable energy and traditional energy companies.',
      image: 'https://images.unsplash.com/photo-1466611653911-95081537e5b7?w=500&h=300&fit=crop',
      tags: ['Energy', 'Sector Focus', 'ESG']
    },
    {
      client: 'Options Trading Strategy',
      category: 'Options Trading',
      return: '+156%',
      period: '9 months',
      description: 'Advanced options strategies including covered calls and protective puts.',
      image: 'https://images.unsplash.com/photo-1590283603385-17ffb3a7f29f?w=500&h=300&fit=crop',
      tags: ['Options', 'Advanced', 'Income']
    },
  ];

  const stats = [
    { icon: FiTrendingUp, value: '156%', label: 'Average Return' },
    { icon: FiUser, value: '500+', label: 'Successful Projects' },
    { icon: FiDollarSign, value: '$50M+', label: 'Assets Managed' },
    { icon: FiCalendar, value: '13', label: 'Years Experience' },
  ];

  const testimonials = [
    {
      name: 'Robert Johnson',
      role: 'Private Investor',
      image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop',
      quote: 'TradePro transformed my investment approach. Their strategic guidance helped me achieve returns I never thought possible.'
    },
    {
      name: 'Maria Rodriguez',
      role: 'Business Owner',
      image: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=100&h=100&fit=crop',
      quote: 'The team\'s expertise in portfolio management has been invaluable for securing my business\'s financial future.'
    },
    {
      name: 'David Chen',
      role: 'Retiree',
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop',
      quote: 'Conservative yet profitable strategies that align perfectly with my retirement goals. Highly recommended!'
    },
  ];

  return (
    <>
      <SEO 
        title="Trading Portfolio & Success Stories - TradePro Client Results"
        description="View our successful trading portfolio results and client success stories. Real returns and proven strategies across stocks, forex, crypto, and more."
        keywords="trading portfolio, investment results, client success stories, trading returns, portfolio performance"
      />
      <div className="pt-16 lg:pt-20">
        {/* Hero Section */}
        <section className="py-20 bg-gradient-to-br from-primary-900 to-primary-700 text-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center"
            >
              <h1 className="text-4xl md:text-5xl font-bold mb-6">Our Portfolio & Success Stories</h1>
              <p className="text-xl text-gray-200 max-w-3xl mx-auto">
                Discover real results from our trading strategies and portfolio management services. See how we've helped clients achieve their financial goals.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
              {stats.map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="text-center"
                >
                  <SafeIcon icon={stat.icon} className="h-12 w-12 text-primary-600 mx-auto mb-4" />
                  <div className="text-3xl font-bold text-gray-900 mb-2">{stat.value}</div>
                  <div className="text-gray-600">{stat.label}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Portfolio Grid */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Featured Success Stories</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Real results from our trading strategies and portfolio management services.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {portfolioItems.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow"
                >
                  <img
                    src={item.image}
                    alt={item.client}
                    className="w-full h-48 object-cover"
                  />
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-medium text-primary-600 bg-primary-50 px-3 py-1 rounded-full">
                        {item.category}
                      </span>
                      <div className="flex items-center text-green-600 font-bold">
                        <SafeIcon icon={FiArrowUpRight} className="h-4 w-4 mr-1" />
                        {item.return}
                      </div>
                    </div>
                    
                    <h3 className="text-lg font-bold text-gray-900 mb-2">{item.client}</h3>
                    <p className="text-gray-600 text-sm mb-4 leading-relaxed">{item.description}</p>
                    
                    <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                      <div className="flex items-center">
                        <SafeIcon icon={FiCalendar} className="h-4 w-4 mr-1" />
                        {item.period}
                      </div>
                    </div>
                    
                    <div className="flex flex-wrap gap-2">
                      {item.tags.map((tag, tagIndex) => (
                        <span
                          key={tagIndex}
                          className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">What Our Clients Say</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Hear from satisfied clients who have achieved their financial goals with our help.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {testimonials.map((testimonial, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                  className="bg-gray-50 p-6 rounded-xl"
                >
                  <div className="flex items-center mb-4">
                    <img
                      src={testimonial.image}
                      alt={testimonial.name}
                      className="w-12 h-12 rounded-full mr-4"
                    />
                    <div>
                      <h4 className="font-semibold text-gray-900">{testimonial.name}</h4>
                      <p className="text-sm text-gray-600">{testimonial.role}</p>
                    </div>
                  </div>
                  <p className="text-gray-700 leading-relaxed italic">"{testimonial.quote}"</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Performance Chart Section */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Performance Overview</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Our track record speaks for itself with consistent performance across different market conditions.
              </p>
            </motion.div>

            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600 mb-2">+156%</div>
                  <div className="text-gray-600">Best Performing Strategy</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary-600 mb-2">89%</div>
                  <div className="text-gray-600">Win Rate</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-secondary-600 mb-2">2.4</div>
                  <div className="text-gray-600">Sharpe Ratio</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-900 mb-2">12%</div>
                  <div className="text-gray-600">Max Drawdown</div>
                </div>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Key Performance Metrics</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Return (3 years):</span>
                    <span className="font-semibold text-green-600">+312%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Annual Volatility:</span>
                    <span className="font-semibold">18.5%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Alpha:</span>
                    <span className="font-semibold text-primary-600">+8.2%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Beta:</span>
                    <span className="font-semibold">0.85</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-primary-900 text-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Ready to Join Our Success Stories?
              </h2>
              <p className="text-xl text-gray-200 mb-8 max-w-2xl mx-auto">
                Start your journey towards financial success with our proven trading strategies and expert guidance.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="#contact"
                  className="inline-flex items-center px-8 py-4 bg-secondary-500 text-white font-semibold rounded-lg hover:bg-secondary-600 transition-colors"
                >
                  Start Your Portfolio
                </a>
                <a
                  href="#consultation"
                  className="inline-flex items-center px-8 py-4 border-2 border-white text-white font-semibold rounded-lg hover:bg-white hover:text-primary-900 transition-colors"
                >
                  Free Consultation
                </a>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
}

export default Portfolio;